import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Edit, Eye, Plus, Search, ChevronLeft, ChevronRight, Package } from 'lucide-react';
import { fetchItemKits, fetchItemKitsCount, fetchCountriesForDropdown } from '../services/api.ts';
import './ItemKits.css';

interface ItemKit {
  id: number;
  name: string;
  avater: string;
  lowest_price: number;
  is_feature: boolean;
  enable: boolean;
  country_id: number;
}

interface Country {
  id: number;
  name: string;
  code: string;
}

const ItemKits: React.FC = () => {
  const navigate = useNavigate();
  const [itemKits, setItemKits] = useState<ItemKit[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const [filterFeature, setFilterFeature] = useState<'all' | 'featured' | 'regular'>('all');
  const [filterCountry, setFilterCountry] = useState<number | undefined>(undefined);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Load countries for dropdown
  useEffect(() => {
    const loadCountries = async () => {
      try {
        const data = await fetchCountriesForDropdown();
        setCountries(data);
      } catch (err) {
        console.error('Error loading countries:', err);
      }
    };
    loadCountries();
  }, []);

  useEffect(() => {
    const loadItemKits = async () => {
      try {
        setLoading(true);
        const offset = (currentPage - 1) * itemsPerPage;
        const [data, count] = await Promise.all([
          fetchItemKits(offset, itemsPerPage, filterCountry),
          fetchItemKitsCount(filterCountry)
        ]);
        setItemKits(data);
        setTotalCount(count);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load item kits');
        console.error('Error loading item kits:', err);
      } finally {
        setLoading(false);
      }
    };

    loadItemKits();
  }, [currentPage, filterCountry, itemsPerPage]);

  const getCountryName = (countryId: number) => {
    const country = countries.find(c => c.id === countryId);
    return country ? country.name : `Country ${countryId}`;
  };

  // Filter and search logic (client-side for currently loaded page)
  const filteredItemKits = itemKits.filter((kit) => {
    const matchesSearch = kit.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus =
      filterStatus === 'all' ||
      (filterStatus === 'active' && kit.enable) ||
      (filterStatus === 'inactive' && !kit.enable);
    const matchesFeature =
      filterFeature === 'all' ||
      (filterFeature === 'featured' && kit.is_feature) ||
      (filterFeature === 'regular' && !kit.is_feature);

    return matchesSearch && matchesStatus && matchesFeature;
  });

  // Pagination
  const totalPages = Math.ceil(totalCount / itemsPerPage);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  if (loading) {
    return (
      <div className="item-kits">
        <div className="loading">
          <div className="loading-spinner"></div>
          <p>Loading item kits...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="item-kits">
        <div className="error">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="item-kits">
      <div className="page-header">
        <h2>Item Kits</h2>
        <div className="page-actions">
          <div className="search-box">
            <Search size={18} />
            <input
              type="text"
              placeholder="Search item kits..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>
          <button className="add-btn" onClick={() => navigate('/dashboard/item-kits/new')}>
            <Plus size={18} />
            Add New Kit
          </button>
        </div>
      </div>

      <div className="filter-section">
        <div className="filter-group">
          <label>Country:</label>
          <select 
            value={filterCountry || ''} 
            onChange={(e) => {
              setFilterCountry(e.target.value ? Number(e.target.value) : undefined);
              setCurrentPage(1);
            }}
          >
            <option value="">All Countries</option>
            {countries.map((country) => (
              <option key={country.id} value={country.id}>
                {country.name}
              </option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <label>Status:</label>
          <select value={filterStatus} onChange={(e) => {
            setFilterStatus(e.target.value as any);
            setCurrentPage(1);
          }}>
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
        <div className="filter-group">
          <label>Type:</label>
          <select value={filterFeature} onChange={(e) => {
            setFilterFeature(e.target.value as any);
            setCurrentPage(1);
          }}>
            <option value="all">All</option>
            <option value="featured">Featured</option>
            <option value="regular">Regular</option>
          </select>
        </div>
        <div className="filter-group">
          <label>Rows:</label>
          <select
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
          >
            <option value={10}>10</option>
            <option value={50}>50</option>
            <option value={75}>75</option>
            <option value={100}>100</option>
          </select>
        </div>
        <div className="filter-group">
          <label>Showing:</label>
          <span style={{ color: '#6366f1', fontWeight: 600 }}>
            {filteredItemKits.length} of {totalCount} item{totalCount !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {filteredItemKits.length === 0 ? (
        <div className="empty-state">
          <p>No item kits found matching your criteria.</p>
        </div>
      ) : (
        <>
          <div className="itemkits-table-container">
            <table className="itemkits-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Image</th>
                  <th>Name</th>
                  <th>Country</th>
                  <th>Lowest Price</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredItemKits.map((kit) => (
                  <tr key={kit.id}>
                    <td className="id-cell">{kit.id}</td>
                    <td className="image-cell">
                      {kit.avater ? (
                        <img
                          src={kit.avater}
                          alt={kit.name}
                          className="kit-thumbnail"
                        />
                      ) : (
                        <div className="kit-placeholder">
                          <Package size={20} />
                        </div>
                      )}
                    </td>
                    <td className="name-cell">
                      <span className="kit-name">{kit.name}</span>
                    </td>
                    <td className="country-cell">
                      {getCountryName(kit.country_id)}
                    </td>
                    <td className="price-cell">
                      <span className="price-tag">${kit.lowest_price.toFixed(2)}</span>
                    </td>
                    <td className="type-cell">
                      <span className={`type-badge ${kit.is_feature ? 'featured' : 'regular'}`}>
                        {kit.is_feature ? 'Featured' : 'Regular'}
                      </span>
                    </td>
                    <td className="status-cell">
                      <span className={`status-badge ${kit.enable ? 'active' : 'inactive'}`}>
                        {kit.enable ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="actions-cell">
                      <button 
                        className="action-btn view" 
                        title="View Items"
                        onClick={() => navigate(`/dashboard/item-kits/view/${kit.id}`)}
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        className="action-btn edit"
                        onClick={() => navigate(`/dashboard/item-kits/edit/${kit.id}`)}
                        title="Edit"
                      >
                        <Edit size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <ChevronLeft size={18} />
                Previous
              </button>

              <div className="pagination-info">
                Page {currentPage} of {totalPages}
              </div>

              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
                <ChevronRight size={18} />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ItemKits;
